import cx from 'classnames';
import type { ReactElement, VoidFunctionComponent } from 'react';

import { LICENSING_ISSUE_TYPE, LICENSING_ISSUE_TYPES } from '../../../../../../../../shared/issuesV2';
import Badge from '../../../../../../generic/Badge/Badge';
import TooltipBadge from '../../../../../../generic/Badge/TooltipBadge';
import { icons } from '../../../../../../generic/Icon/icons';
import { Icon } from '../../../../../../generic/Icon/index';
import { isIssueOfActiveType } from '../../helpers';
import type { Dependency } from '../../../../../../../../shared/dependenciesV2';
import type { LicensingIssueType } from '../../../../../../../../shared/issuesV2';
import type { BadgeColor } from '../../../../../../generic/Badge/Badge';
import type { IconClassName } from '../../../../../../generic/Icon/icons';

import styles from '../DetailsRow.module.less';

export const IssueColors: Partial<Record<LicensingIssueType, BadgeColor>> = {
  [LICENSING_ISSUE_TYPE.PolicyConflict]: 'light-red',
  [LICENSING_ISSUE_TYPE.PolicyFlag]: 'light-yellow',
  [LICENSING_ISSUE_TYPE.UnlicensedDependency]: 'light-gray',
  [LICENSING_ISSUE_TYPE.UnconcludedDependency]: 'medium-yellow',
  [LICENSING_ISSUE_TYPE.MultiLicenseConcluded]: 'medium-yellow',
};

export const IssueNames: Partial<Record<LicensingIssueType, string>> = {
  [LICENSING_ISSUE_TYPE.PolicyConflict]: 'Denied',
  [LICENSING_ISSUE_TYPE.PolicyFlag]: 'Flagged',
};

/**
 * Defines licenses which rich data: declared and issue type
 */
type RichLicense = {
  licenseId: string;
  isDeclared: boolean;
  issueType: LicensingIssueType;
};

export const unconcludedRichLicense: RichLicense = {
  licenseId: 'No concluded license',
  isDeclared: false,
  issueType: LICENSING_ISSUE_TYPE.UnconcludedDependency,
};

/**
 * Unlicensed richLicense is a special case for when there is no licenses on the dep
 */
export const unlicensedRichLicense: RichLicense = {
  licenseId: 'Unlicensed',
  isDeclared: false,
  issueType: LICENSING_ISSUE_TYPE.UnlicensedDependency,
};

/**
 * Builds rich license from dep data
 * @param {string} licenseId License ID
 * @param {string[]} declaredLicenses list of declared licenses
 * @param {DependencyIssue[]} issues issues on the dependency
 * @returns {RichLicense} rich license object
 */
export const buildRichLicense = (
  licenseId: string,
  declaredLicenses: string[],
  issues: Dependency['issues'],
): RichLicense => ({
  licenseId,
  isDeclared: declaredLicenses.includes(licenseId),
  issueType: issues.find((issue) => isIssueOfActiveType(issue, LICENSING_ISSUE_TYPES) && issue.license === licenseId)
    ?.type as LicensingIssueType,
});

/**
 * Sorts RichLicense array by declared first then alphabetical
 * @param {RichLicense} a comparison license
 * @param {RichLicense} b comparison license
 * @returns {number} Comparison result
 */
export const sortDeclaredLicenseFirst = (a: RichLicense, b: RichLicense) => {
  if (a.isDeclared && !b.isDeclared) {
    return -1;
  }

  if (b.isDeclared && !a.isDeclared) {
    return 1;
  }

  return a.licenseId.localeCompare(b.licenseId);
};

type LicensingBadgeProps = {
  license: RichLicense;
};

/**
 * License badge displays a license with an icon if it is declared and
 * colors which indicate an issue type
 * @param {LicensingBadgeProps} props Rich License
 * @returns License badge
 */
export const LicensingBadge: VoidFunctionComponent<LicensingBadgeProps> = ({
  license,
}: LicensingBadgeProps): ReactElement => {
  const isDeclaredIcon = (license.isDeclared && icons.circleStar) || undefined;
  const issueColor = (license.issueType && IssueColors[license.issueType]) || 'white';
  const tooltip = `${license.isDeclared ? 'Declared License - ' : ''}${license.licenseId}${license.issueType && IssueNames[license.issueType] ? `, ${IssueNames[license.issueType]}` : ''}`;

  return (
    <TooltipBadge
      color={issueColor}
      icon={isDeclaredIcon}
      id={`license-${license.licenseId}`}
      tooltip={tooltip}
      fullWidth
    >
      {license.licenseId}
    </TooltipBadge>
  );
};

export const ConcludedLicensingBadge: VoidFunctionComponent<LicensingBadgeProps> = ({ license }) => {
  const issueColor =
    license.issueType === LICENSING_ISSUE_TYPE.UnconcludedDependency ||
    license.issueType === LICENSING_ISSUE_TYPE.UnlicensedDependency
      ? IssueColors[license.issueType]
      : 'white';

  // Default to a checkmark on a green badge to represent an approved license.
  let icon: IconClassName | undefined;

  switch (license.issueType) {
    case 'policy_flag':
      icon = icons.flagOutline;
      break;
    case 'policy_conflict':
      icon = icons.banOutline;
      break;
    case 'unconcluded_dependency':
      break;
    case 'unlicensed_dependency':
      break;
    default:
      icon = icons.checkCircleO;
      break;
  }

  return (
    <Badge color={issueColor || 'white'}>
      <div className={styles.iconBadge}>
        <div
          className={cx({
            [styles.flagged]: license.issueType === LICENSING_ISSUE_TYPE.PolicyFlag,
            [styles.denied]: license.issueType === LICENSING_ISSUE_TYPE.PolicyConflict,
            [styles.approved]: !(
              license.issueType === LICENSING_ISSUE_TYPE.UnconcludedDependency ||
              license.issueType === LICENSING_ISSUE_TYPE.UnlicensedDependency
            ),
          })}
        >
          {icon && <Icon icon={icon} />}
        </div>
        {license.licenseId}
      </div>
    </Badge>
  );
};
